import { useMemo, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { ArrowLeft, ExternalLink, FileText, Plus, RefreshCw, Upload } from 'lucide-react'
import {
  Button,
  Chip,
  DataTable,
  EmptyState,
  Modal,
  PageHeaderBand,
  QueryErrorState,
  Select,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  toast,
} from '@/components/ui'
import { PanelCard } from '@/shell/forms/FormKit'
import { useHasAnyPermission } from '@/shell/module-system'
import { documentUrl } from '@/shell/services/cofa-registry/utils/document-url'
import { odooUrl } from '@/lib/utils/odoo-url'
import {
  useArchiveShippingDocument,
  useCancelDispatch,
  useChangeDocumentType,
  usePoShippingDocuments,
  useUpdateDispatch,
} from '../api/shipping-docs-queries'
import type { Dispatch, DispatchStatus, ShippingDocument } from '../api/shipping-docs-types'
import { DispatchFormModal } from '../components/DispatchFormModal'
import { ShippingDocTypesPanel } from '../components/ShippingDocTypesPanel'
import { UploadShippingDocumentDrawer } from '../components/UploadShippingDocumentDrawer'

/** Same roles as the module's `permissions` and the middleware `_WRITE_ROLES`. */
const WRITE_ROLES = ['admin', 'operations', 'operations_logistics', 'purchasing'] as const

const STATUS_LABEL: Record<DispatchStatus, string> = {
  planned: 'Planned',
  shipped: 'Shipped',
  received: 'Received',
  cancelled: 'Cancelled',
}
const STATUS_VARIANT: Record<DispatchStatus, 'neutral' | 'info' | 'success' | 'warning'> = {
  planned: 'neutral',
  shipped: 'info',
  received: 'success',
  cancelled: 'warning',
}

/** "2026-10-15" -> "Oct 15, 2026" (month spelled out, never ambiguous). */
function fmt(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return iso
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  })
}

/**
 * Shipping documents for one PO, one card per dispatch. A dispatch exists
 * without any Odoo receipt, so documents for an upcoming shipment can be
 * filed before the backorder is created. Odoo's PO "Shipping Documents" tab
 * links here and shows one row per dispatch with its documents.
 */
export function PoShippingDocumentsPage() {
  const params = useParams({ strict: false }) as { odooPoId?: string }
  const poId = params.odooPoId && /^\d+$/.test(params.odooPoId) ? Number(params.odooPoId) : null
  const navigate = useNavigate()
  const canWrite = useHasAnyPermission([...WRITE_ROLES])
  const isAdmin = useHasAnyPermission(['admin'])

  const { data, isLoading, isError, error, refetch, isFetching } = usePoShippingDocuments(poId)
  const update = useUpdateDispatch()
  const cancel = useCancelDispatch()
  const archive = useArchiveShippingDocument()
  const retype = useChangeDocumentType()

  const [formTarget, setFormTarget] = useState<Dispatch | 'new' | null>(null)
  const [formNonce, setFormNonce] = useState(0)
  const [uploadTarget, setUploadTarget] = useState<Dispatch | null>(null)
  const [uploadNonce, setUploadNonce] = useState(0)
  const [cancelTarget, setCancelTarget] = useState<Dispatch | null>(null)

  const columns = useMemo<ColumnDef<ShippingDocument, unknown>[]>(
    () => [
      {
        id: 'type',
        header: 'Document type',
        cell: (ctx) =>
          canWrite && (data?.types?.length ?? 0) > 0 ? (
            <Select
              aria-label={`Type of ${ctx.row.original.filename}`}
              value={ctx.row.original.doc_type_key}
              onChange={(e) =>
                retype.mutate(
                  { documentId: ctx.row.original.id, docTypeKey: e.target.value },
                  {
                    onSuccess: () => toast.success('Type changed'),
                    onError: (err) => toast.danger('Could not change the type', { description: (err as Error).message }),
                  },
                )
              }
            >
              {!data?.types?.some((t) => t.key === ctx.row.original.doc_type_key) ? (
                <option value={ctx.row.original.doc_type_key}>{ctx.row.original.doc_type_label}</option>
              ) : null}
              {data?.types?.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </Select>
          ) : (
            ctx.row.original.doc_type_label
          ),
      },
      {
        id: 'file',
        header: 'File',
        cell: (ctx) => (
          <a
            className="inline-flex items-center gap-1 text-accent-text hover:underline"
            href={documentUrl(ctx.row.original.document_id, ctx.row.original.filename)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FileText size={12} /> {ctx.row.original.filename}
          </a>
        ),
      },
      {
        id: 'uploaded',
        header: 'Filed',
        cell: (ctx) => (
          <div className="leading-snug">
            <div>{fmt(ctx.row.original.created_at)}</div>
            <div className="text-sm text-secondary">{ctx.row.original.uploaded_by ?? '—'}</div>
          </div>
        ),
      },
      { id: 'notes', header: 'Notes', cell: (ctx) => ctx.row.original.notes ?? '—' },
      ...(canWrite
        ? [{
            id: 'actions',
            header: '',
            cell: (ctx) => (
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    archive.mutate(ctx.row.original.id, {
                      onSuccess: () => toast.success('Document archived'),
                      onError: (err) => toast.danger('Archive failed', { description: (err as Error).message }),
                    })
                  }
                >
                  Archive
                </Button>
              </div>
            ),
          } satisfies ColumnDef<ShippingDocument, unknown>]
        : []),
    ],
    [canWrite, archive, retype, data?.types],
  )

  if (poId == null) {
    return (
      <div className="p-6">
        <QueryErrorState error={new Error('Invalid purchase order id in the URL')} subject="shipping documents" />
      </div>
    )
  }

  const po = data?.po
  const dispatches = data?.dispatches ?? []
  const types = data?.types ?? []
  const balances = data?.balances ?? []
  const takenReceipts = new Set(dispatches.map((d) => d.odoo_picking_id).filter((x): x is number => x != null))

  const setReceipt = (d: Dispatch, value: string) =>
    update.mutate(
      { dispatchId: d.id, body: { odoo_picking_id: value ? Number(value) : null } },
      { onError: (err) => toast.danger('Could not link receipt', { description: (err as Error).message }) },
    )

  const setStatus = (d: Dispatch, status: DispatchStatus) =>
    update.mutate(
      { dispatchId: d.id, body: { status } },
      { onError: (err) => toast.danger('Could not update status', { description: (err as Error).message }) },
    )

  const openForm = (target: Dispatch | 'new') => {
    setFormTarget(target)
    setFormNonce((n) => n + 1)
  }

  return (
    <div data-density="comfortable" data-surface="form" className="flex h-full flex-col">
      <PageHeaderBand
        title={po ? `${po.name} shipping documents` : 'Shipping documents'}
        description="Vendor packing lists, invoices, BLs and CoAs, grouped by dispatch. Odoo's PO Shipping Documents tab links to each file."
      >
        <div className="ml-auto flex items-center gap-2">
          {po?.vendor ? <Chip variant="neutral">{po.vendor}</Chip> : null}
        </div>
      </PageHeaderBand>

      <Toolbar>
        <ToolbarLeft>
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/po-ship-confirmation' })}>
            <ArrowLeft size={14} /> Ship confirmation board
          </Button>
        </ToolbarLeft>
        <ToolbarRight>
          <Button variant="ghost" size="sm" onClick={() => window.open(odooUrl('purchase.order', poId), '_blank', 'noopener')}>
            <ExternalLink size={14} /> Open in Odoo
          </Button>
          <Button variant="ghost" size="sm" iconOnly aria-label="Refresh" loading={isFetching && !isLoading} onClick={() => void refetch()}>
            <RefreshCw size={14} />
          </Button>
          {canWrite ? (
            <Button variant="primary" size="sm" onClick={() => openForm('new')} disabled={!po}>
              <Plus size={14} /> Add dispatch
            </Button>
          ) : null}
        </ToolbarRight>
      </Toolbar>

      <div className="min-h-0 flex-1 overflow-auto bg-canvas px-6 py-6">
        <div className="mx-auto flex w-full max-w-[calc(var(--form-content-width)+var(--form-aside-width))] flex-col gap-6">
          {isError ? (
            <QueryErrorState error={error} onRetry={() => void refetch()} subject="shipping documents" />
          ) : isLoading ? (
            <div className="text-secondary">Loading…</div>
          ) : dispatches.length === 0 ? (
            <EmptyState
              title="No dispatches yet"
              description="Add a dispatch for each vendor shipment, then file its documents. This works before Odoo has a receipt or backorder for it."
            />
          ) : (
            <>
              {balances.length > 0 && dispatches.some((x) => x.lines.length > 0) ? (
                <PanelCard title="Order balance">
                  <table className="w-full text-md">
                    <thead>
                      <tr className="text-left text-secondary">
                        <th className="py-1 font-medium">Line</th>
                        <th className="py-1 text-right font-medium">Ordered</th>
                        <th className="py-1 text-right font-medium">On dispatches</th>
                        <th className="py-1 text-right font-medium">Still open</th>
                      </tr>
                    </thead>
                    <tbody>
                      {balances.map((b) => (
                        <tr key={b.odoo_po_line_id}>
                          <td className="py-1">{b.product_name}</td>
                          <td className="py-1 text-right">{Number(b.ordered)} {b.uom ?? ''}</td>
                          <td className="py-1 text-right">{Number(b.dispatched)}</td>
                          <td className="py-1 text-right font-medium">{Number(b.open)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </PanelCard>
              ) : null}
            {dispatches.map((d) => (
              <PanelCard key={d.id} title={`${d.label} · ship ${fmt(d.ship_date)}`}>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-md text-secondary">
                  <Chip variant={STATUS_VARIANT[d.status]}>{STATUS_LABEL[d.status]}</Chip>
                  <span>ETA {fmt(d.eta_date)}</span>
                  {d.reference ? <span>Ref: {d.reference}</span> : null}
                  {d.carrier ? <span>Carrier: {d.carrier}</span> : null}
                  <label className="ml-auto flex items-center gap-2">
                    <span>Receipt</span>
                    <Select
                      aria-label={`Receipt for ${d.label}`}
                      value={d.odoo_picking_id ?? ''}
                      disabled={!canWrite || d.status === 'cancelled'}
                      onChange={(e) => setReceipt(d, e.target.value)}
                    >
                      <option value="">— not linked —</option>
                      {(po?.receipts ?? []).map((r) => (
                        <option key={r.id} value={r.id} disabled={takenReceipts.has(r.id) && r.id !== d.odoo_picking_id}>
                          {r.name}
                          {r.backorder_of ? ` (backorder of ${r.backorder_of})` : ''} · {r.state}
                        </option>
                      ))}
                    </Select>
                  </label>
                </div>
                {d.notes ? <p className="text-md text-secondary">{d.notes}</p> : null}
                {d.lines.length > 0 ? (
                  <ul className="flex flex-col gap-0.5 text-md">
                    {d.lines.map((l) => (
                      <li key={l.id} className="flex items-center gap-2">
                        <span className="font-medium">{l.product_name ?? `Line ${l.odoo_po_line_id}`}</span>
                        <span className="text-secondary">{Number(l.quantity)}</span>
                        {l.confirmed ? <Chip variant="success">Ship date confirmed</Chip> : null}
                      </li>
                    ))}
                  </ul>
                ) : null}

                {canWrite && d.status !== 'cancelled' ? (
                  <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" size="sm" onClick={() => { setUploadTarget(d); setUploadNonce((n) => n + 1) }}>
                      <Upload size={14} /> Upload documents
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openForm(d)}>Edit</Button>
                    {d.status === 'planned' ? (
                      <Button variant="ghost" size="sm" onClick={() => setStatus(d, 'shipped')}>Mark shipped</Button>
                    ) : null}
                    {d.status !== 'received' ? (
                      <Button variant="ghost" size="sm" onClick={() => setStatus(d, 'received')}>Mark received</Button>
                    ) : null}
                    <Button variant="ghost" size="sm" onClick={() => setCancelTarget(d)}>Cancel dispatch</Button>
                  </div>
                ) : null}

                <DataTable
                  data={d.documents}
                  columns={columns}
                  getRowId={(r) => String(r.id)}
                  moduleId="po-shipping-docs"
                  stickyFirstColumn={false}
                  maxHeight="none"
                />
              </PanelCard>
            ))}
            </>
          )}

          {isAdmin ? (
            <PanelCard title="Shipping document types">
              <ShippingDocTypesPanel />
            </PanelCard>
          ) : null}
        </div>
      </div>

      {po && formTarget ? (
        <DispatchFormModal
          key={formNonce}
          open
          onOpenChange={(o) => !o && setFormTarget(null)}
          odooPoId={poId}
          dispatch={formTarget === 'new' ? null : formTarget}
          balances={balances}
        />
      ) : null}
      {uploadTarget ? (
        <UploadShippingDocumentDrawer
          key={uploadNonce}
          open
          onOpenChange={(o) => !o && setUploadTarget(null)}
          dispatchId={uploadTarget.id}
          dispatchLabel={uploadTarget.label}
          types={types}
        />
      ) : null}

      <Modal
        open={cancelTarget != null}
        onOpenChange={(o) => !o && setCancelTarget(null)}
        tone="warning"
        title={`Cancel ${cancelTarget?.label ?? 'dispatch'}?`}
        description={
          cancelTarget && cancelTarget.documents.length > 0
            ? `${cancelTarget.documents.length} filed document(s) stay in the library but disappear from Odoo's tab.`
            : 'The dispatch is removed from Odoo’s Shipping Documents tab.'
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCancelTarget(null)}>Keep</Button>
            <Button
              variant="danger"
              loading={cancel.isPending}
              onClick={() => {
                if (!cancelTarget) return
                cancel.mutate(
                  { dispatchId: cancelTarget.id, force: cancelTarget.documents.length > 0 },
                  {
                    onSuccess: () => { toast.success(`${cancelTarget.label} cancelled`); setCancelTarget(null) },
                    onError: (err) => toast.danger('Cancel failed', { description: (err as Error).message }),
                  },
                )
              }}
            >
              Cancel dispatch
            </Button>
          </div>
        }
      />
    </div>
  )
}
